/**
 * Time windows for counting parts on the current labor ticket.
 *
 * Uses the same scheduled/unscheduled job windows as Utilization ("You have made"),
 * but only from when the operator clocked in. Example: job started at 8:00, clock-in at 9:00
 * → count parts from 9:00 onward.
 *
 * When multiple jobs overlap the ticket window, parts are counted once per window.
 */
import { createSelector } from 'reselect';
import { chopBefore, sortIntervalsByStart } from 'lib/utils/intervals';
import {
  getScopeScheduledActivityIntervals,
  getScopeUnscheduledActivityIntervals,
} from 'lib/selectors/getScopeScheduledActivityIntervals';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getScopeEnd } from 'lib/selectors/getScopeEnd';

const getLaborPartCountQueryIntervals = createSelector(
  [
    getScopeScheduledActivityIntervals,
    getScopeUnscheduledActivityIntervals,
    getCurrentLaborTicket,
    getScopeEnd,
  ],
  (scheduledIntervals, unscheduledIntervals, currentLaborTicket, scopeEnd) => {
    const clockIn = currentLaborTicket?.clockIn;
    if (!clockIn) {
      return null;
    }

    // Scope selectors emit newest-first; chopBefore expects start ASC.
    const choppedScheduled = chopBefore(
      [...scheduledIntervals].sort(sortIntervalsByStart),
      clockIn
    );
    const choppedUnscheduled = chopBefore(
      [...unscheduledIntervals].sort(sortIntervalsByStart),
      clockIn
    );

    if (!choppedScheduled.length && !choppedUnscheduled.length) {
      return {
        scheduledIntervals: [],
        unscheduledIntervals: [{ start: clockIn, end: scopeEnd || null }],
      };
    }

    return {
      scheduledIntervals: choppedScheduled,
      unscheduledIntervals: choppedUnscheduled,
    };
  }
);

export { getLaborPartCountQueryIntervals };
